"use client";

import { useState } from "react";

type TabId = "engagement" | "knowledge" | "performance" | "physical";

interface TabContent {
  leftTitle: string;
  leftSubtitle: string;
  leftDescription: string;
  card1Title: string;
  card1Items: string[];
  card2Title: string;
  card2Items: string[];
  bullets: string[];
  linkText: string;
  flowTitle: string;
  flowSteps: { label: string, sub: string }[];
}

const tabsData: { id: TabId, label: string }[] = [
  { id: "engagement", label: "Engagement" },
  { id: "knowledge", label: "Knowledge Management" },
  { id: "performance", label: "Performance Management" },
  { id: "physical", label: "Physical → Digital" },
];

const tabContent: Record<TabId, TabContent> = {
  engagement: {
    leftTitle: "MaxAI + PhygitalMax",
    leftSubtitle: "",
    leftDescription:
      "Human teams and AI agents work together across assisted, digital, video, and physical channels to convert, retain, and grow customers at scale.",
    card1Title: "HUMAN WORKFORCE",
    card1Items: [
      "Agent Assist Cockpit",
      "Human in the Loop",
      "Continuous Learning & Feedback Loop",
    ],
    card2Title: "AI WORKFORCE",
    card2Items: [
      "Agentic AI Agents",
      "AI Agents",
      "Digital Agents",
      "Video Bots",
    ],
    bullets: [
      "Agentic AI handles tier-1 queries autonomously",
      "Human-in-the-loop escalation with full context handoff",
      "Conversational chatbots and video bots for D2C acquisition",
      "Continuous learning loop from every resolved interaction",
    ],
    linkText: "Explore PhygitalMax",
    flowTitle: "ENGAGEMENT FLOW",
    flowSteps: [
      {
        label: "Customer Touchpoint",
        sub: "Web · App · Voice · Video · In-store",
      },
      { label: "AI Agent (MaxAI)", sub: "Handles ~60% queries autonomously" },
      {
        label: "Human Agent + Cockpit",
        sub: "Full context, guided by KnowMax",
      },
      {
        label: "Continuous Learning Loop",
        sub: "AI + knowledge improves every cycle",
      },
    ],
  },
  knowledge: {
    leftTitle: "KnowMax",
    leftSubtitle: "",
    leftDescription:
      "Knowledge becomes a growth asset. What works gets amplified. What doesn't gets fixed automatically, so every agent gets smarter over time.",
    card1Title: "Knowledge Management",
    card1Items: ["Decision Trees", "Picture Guides", "FAQs", "Gen-AI Search"],
    card2Title: "Learning Management",
    card2Items: ["Learning Paths", "Gamification"],
    bullets: [],
    linkText: "Explore KnowMax",
    flowTitle: "KNOWLEDGE FLOW",
    flowSteps: [
      {
        label: "Content Ingestion",
        sub: "SOPs, FAQs, decision trees migrated in",
      },
      {
        label: "Gen-AI Search Layer",
        sub: "Agents find answers in seconds",
      },
      { label: "Learning Management", sub: "Paths, gamification, assessments" },
      {
        label: "Quality & Sentiment QA",
        sub: "Evaluations close the content feedback loop",
      },
    ],
  },
  performance: {
    leftTitle: "LeapMax + WorkMax",
    leftSubtitle: "",
    leftDescription:
      "Productivity, security, and compliance are built in, ensuring growth scales without leakage or risk.",
    card1Title: "Productivity Management",
    card1Items: ["App & Web Usage", "Productivity Stats", "Agent Activity"],
    card2Title: "Infosec Management",
    card2Items: [
      "Device Kiosk Mode",
      "Content Protection",
      "Leap OS Personal & Official",
      "Restrict Usage",
    ],
    bullets: [],
    linkText: "Explore LeapMax",
    flowTitle: "PERFORMANCE FLOW",
    flowSteps: [
      {
        label: "Agent Activity Capture",
        sub: "App/web usage, productivity stats live",
      },
      {
        label: "InfoSec & Kiosk Mode",
        sub: "Content protection and device control",
      },
      {
        label: "Compliance Engine",
        sub: "AI SOP adherence + screen recorder",
      },
      {
        label: "Profitability Protection",
        sub: "Leakage flagged and corrected in real-time",
      },
    ],
  },
  physical: {
    leftTitle: "DeviceMax",
    leftSubtitle: "",
    leftDescription:
      "Physical data points fuel digital decisions. Real-world environments feed intelligence back into the system, closing the loop between experience and outcome.",
    card1Title: "Business Efficiency",
    card1Items: ["Revenue Enhancement · People Management · Pilferage Control"],
    card2Title: "Sustainability",
    card2Items: [
      "Energy Efficiency",
      "Wastage Control",
      "Reduced Carbon Footprint",
    ],
    bullets: [],
    linkText: "Explore DeviceMax",
    flowTitle: "Physical Sensor Layer",
    flowSteps: [
      {
        label: "Physical Sensor Layer",
        sub: "CCTV, fire, access, energy systems",
      },
      {
        label: "Edge / Cloud Analytics",
        sub: "Video AI processed at source or in cloud",
      },
      {
        label: "Digital System Sync",
        sub: "Feeds engagement and performance layers",
      },
      {
        label: "Business Intelligence",
        sub: "Revenue, safety, sustainability decisions",
      },
    ],
  },
};

export default function AITransformationSection() {
  const [activeTab, setActiveTab] = useState<TabId>("engagement");

  const content = tabContent[activeTab];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Label */}
        <p
          className="text-xs font-semibold uppercase tracking-widest mb-5"
          style={{ color: "var(--color-accent)" }}
        >
          AI-LED TRANSFORMATION ARCHITECTURE
        </p>

        {/* Main Heading */}
        <h2
          className="text-4xl sm:text-5xl font-bold leading-tight mb-5"
          style={{ color: "#000", fontFamily: "'Titillium Web', sans-serif" }}
        >
          Built to deliver real business outcomes
        </h2>

        {/* Description */}
        <p
          className="text-base sm:text-lg leading-relaxed mb-10 max-w-2xl"
          style={{ color: "var(--color-muted-foreground)" }}
        >
          Four interconnected capabilities, not four separate tools. Each layer
          feeds intelligence back into the others.
        </p>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-10 overflow-x-auto pb-1">
          {tabsData.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap text-black border-black"
              style={
                activeTab === tab.id
                  ? {
                      background: "var(--color-accent)",
                      color: "#fff",
                      border: "1.5px solid var(--color-accent)",
                    }
                  : {
                      background: "transparent",
                      color: "var(--color-accent)",
                      border: "1.5px solid var(--color-accent)",
                    }
              }
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* 2-Column Content */}
        <div key={activeTab} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* LEFT SIDE */}
          <div>
            <h3
              className="text-2xl sm:text-3xl font-bold mb-3"
              style={{
                color: "#000",
                fontFamily: "'Titillium Web', sans-serif",
              }}
            >
              {content.leftTitle}
            </h3>
            <p
              className="text-base leading-relaxed mb-6"
              style={{ color: "var(--color-muted-foreground)" }}
            >
              {content.leftDescription}
            </p>

            {/* Two Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {/* Card 1 */}
              <div
                className="rounded-xl p-4"
                style={{
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                }}
              >
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-3"
                  style={{ color: "var(--color-muted-foreground)" }}
                >
                  {content.card1Title}
                </p>
                <div className="flex flex-col gap-1.5">
                  {content.card1Items.map((item, i) => (
                    <span
                      key={i}
                      className="text-sm"
                      style={{ color: "var(--color-foreground)" }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* Card 2 */}
              <div
                className="rounded-xl p-4"
                style={{
                  background: "#0f1a2e",
                  border: "1px solid #1e3050",
                }}
              >
                <p
                  className="text-xs font-bold uppercase tracking-widest mb-3"
                  style={{ color: "var(--color-accent)" }}
                >
                  {content.card2Title}
                </p>
                <div className="flex flex-col gap-1.5">
                  {content.card2Items.map((item, i) => (
                    <span
                      key={i}
                      className="text-sm"
                      style={{ color: "#cbd5e1" }}
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Bullet Points */}
            <ul className="flex flex-col gap-3 mb-6">
              {content.bullets.map((bullet, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span
                    className="mt-1 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ background: "var(--color-accent)" }}
                  >
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1.5 4L3.5 6L6.5 2"
                        stroke="#fff"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span
                    className="text-sm leading-relaxed"
                    style={{ color: "var(--color-foreground)" }}
                  >
                    {bullet}
                  </span>
                </li>
              ))}
            </ul>

            {/* Link */}
            <a
              href="#"
              className="inline-flex items-center gap-1 text-sm font-semibold transition-opacity hover:opacity-80"
              style={{ color: "var(--color-accent)" }}
            >
              {content.linkText}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M3 8H13M13 8L9 4M13 8L9 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>

          {/* RIGHT SIDE — Flow Card */}
          <div
            className="rounded-2xl p-6 sm:p-8"
            style={{
              background: "#0f1a2e",
              border: "1px solid #1e3050",
              boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
            }}
          >
            <p
              className="text-xs font-bold uppercase tracking-widest mb-8"
              style={{ color: "var(--color-accent)" }}
            >
              {content.flowTitle}
            </p>

            <div className="flex flex-col gap-0">
              {content.flowSteps.map((step, i) => (
                <div key={i} className="flex flex-col">
                  {/* Step Box */}
                  <div
                    className="rounded-xl px-5 py-4"
                    style={{
                      background: i === 0 ? "var(--color-accent)" : "#1a2d4a",
                      border: i === 0 ? "none" : "1px solid #2a4060",
                    }}
                  >
                    <p
                      className="text-sm font-semibold mb-0.5"
                      style={{ color: i === 0 ? "#fff" : "#e2e8f0" }}
                    >
                      {step.label}
                    </p>
                    <p
                      className="text-xs"
                      style={{
                        color: i === 0 ? "rgba(255,255,255,0.8)" : "#94a3b8",
                      }}
                    >
                      {step.sub}
                    </p>
                  </div>

                  {/* Arrow connector */}
                  {i < content.flowSteps.length - 1 && (
                    <div className="flex justify-center py-2">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10 3V17M10 17L5 12M10 17L15 12"
                          stroke="var(--color-accent)"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* MAX-AI Suite Black Card Block */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div
          className="rounded-2xl p-8 sm:p-10"
          style={{
            background: "#000",
            color: "#fff",
            boxShadow: "0 8px 32px rgba(0,0,0,0.28)",
          }}
        >
          {/* Top Label */}
          <p
            className="text-xs font-semibold uppercase tracking-widest mb-4"
            style={{ color: "rgba(255,255,255,0.55)", letterSpacing: "0.15em" }}
          >
            MAX-AI Suite
          </p>

          {/* Main Heading */}
          <h3
            className="text-2xl sm:text-3xl font-bold mb-4"
            style={{
              color: "#fff",
              fontFamily: "'Titillium Web', sans-serif",
            }}
          >
            The AI layer powering every interaction
          </h3>

          {/* Description */}
          <p
            className="text-sm sm:text-base leading-relaxed mb-8 max-w-2xl"
            style={{ color: "rgba(255,255,255,0.65)" }}
          >
            A walled-garden, enterprise-grade AI suite purpose-built for regulated industries. Runs across every product in the UnifyMax ecosystem.
          </p>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-3 mb-8">
            {[
              { emoji: "🤖", label: "AI Agents" },
              { emoji: "💬", label: "Conversational Chatbots" },
              { emoji: "📹", label: "Video Bots" },
              { emoji: "✍️", label: "Content Generation" },
              { emoji: "🔒", label: "Walled-Garden LLMs" },
            ].map((pill) => (
              <span
                key={pill.label}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(255,255,255,0.06)",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                <span>{pill.emoji}</span>
                {pill.label}
              </span>
            ))}
          </div>

          {/* Divider */}
          <div
            className="mb-6"
            style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}
          />

          {/* Touchpoints + Use Cases Row */}
          <div className="flex flex-wrap items-center gap-6">
            {/* Touchpoints */}
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="text-xs font-semibold uppercase tracking-widest"
                style={{ color: "rgba(255,255,255,0.45)", letterSpacing: "0.12em" }}
              >
                Touchpoints
              </span>
              {["Assisted", "Digital", "Physical", "AI"].map((pill) => (
                <span
                  key={pill}
                  className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium"
                  style={{
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(255,255,255,0.06)",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  {pill}
                </span>
              ))}
            </div>

            {/* Use Cases */}
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="text-xs font-semibold uppercase tracking-widest"
                style={{ color: "rgba(255,255,255,0.45)", letterSpacing: "0.12em" }}
              >
                Use Cases
              </span>
              {["D2C Acquisition", "LTV Management"].map((pill) => (
                <span
                  key={pill}
                  className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium"
                  style={{
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(255,255,255,0.06)",
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  {pill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
